import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";

dayjs.extend(utc);
dayjs.extend(timezone);

export type DropStatus = "prelaunch" | "live" | "ended";

export type StorefrontAvailabilityPeriod = {
  type: "continuous" | "scheduled";
  startDate: Date | null;
  endDate: Date | null;
};

// startDate/endDate are stored as timestamptz, i.e. real UTC instants, so
// these comparisons are plain instant comparisons — no explicit timezone
// anchoring (e.g. .tz("Europe/London")) is needed here, unlike
// deliveryAreaCommons.ts which anchors to a local *calendar day*.
export const isStorefrontAvailableAt = (
  now: Date,
  availability: StorefrontAvailabilityPeriod,
): boolean => {
  if (availability.type === "continuous") {
    return true;
  }

  if (!availability.startDate || !availability.endDate) {
    return false;
  }

  const nowDayjs = dayjs(now);
  return (
    !nowDayjs.isBefore(availability.startDate) &&
    !nowDayjs.isAfter(availability.endDate)
  );
};

export const getDropStatus = (
  now: Date,
  availability: Pick<StorefrontAvailabilityPeriod, "startDate" | "endDate">,
): DropStatus => {
  const nowDayjs = dayjs(now);

  if (availability.startDate && nowDayjs.isBefore(availability.startDate)) {
    return "prelaunch";
  }

  if (availability.endDate && nowDayjs.isAfter(availability.endDate)) {
    return "ended";
  }

  return "live";
};

// "Days remaining" is presentation copy anchored to UK calendar days, so
// (unlike the instant comparisons above) this must anchor to Europe/London
// before diffing. dayjs's .diff(..., "day") is duration-based (elapsed ms /
// 86400000), not calendar-aware, so anchoring alone isn't enough: on the
// 23-hour spring-forward day it would still undercount and on the 25-hour
// autumn-back day it would overcount when the server process runs in UTC
// (as it does in production). Instead, resolve each instant to its
// Europe/London calendar date and diff those dates in a DST-free (UTC)
// frame, which always yields an exact whole-day count.
export const getDaysUntilDropEnds = (now: Date, endDate: Date): number => {
  const nowLondonDate = dayjs(now).tz("Europe/London").format("YYYY-MM-DD");
  const endLondonDate = dayjs(endDate).tz("Europe/London").format("YYYY-MM-DD");
  return dayjs.utc(endLondonDate).diff(dayjs.utc(nowLondonDate), "day");
};
